import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../utils/api';
import backgroundVideo from '../assets-material/videos/6100414-uhd-2160-4096-30fps_Fqzu8BY8.mp4';

const RESEND_COOLDOWN_SECONDS = 60;

// Map backend API messages (English) to i18n keys so errors/success show in current language
const BACKEND_MESSAGE_KEYS = {
  'Property owner registered successfully. Please check your email to verify your account.': 'registerOwner.successMessage',
  'Failed to register property owner': 'registerOwner.errorFailed',
  'Email already taken.': 'registerOwner.errorEmailTaken',
  'Property Owner role not found. Please contact administrator.': 'registerOwner.errorRoleNotFound',
  'Please verify your email before logging in. Check your inbox for the verification link.': 'registerOwner.errorVerifyEmail',
};

const RegisterOwner = () => {
  const { t, i18n } = useTranslation();
  const { language } = useLanguageCurrency();

  const getTranslatedMessage = (apiMessage) => {
    if (!apiMessage || typeof apiMessage !== 'string') return apiMessage || t('registerOwner.errorGeneric');
    const trimmed = apiMessage.trim();
    const key = BACKEND_MESSAGE_KEYS[trimmed];
    return key ? t(key) : trimmed;
  };
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    // Set initial value
    setIsDesktop(window.innerWidth >= 1024);
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    countryCode: '+20',
    nationality: '',
    role: 'property_owner', // Property owner registration only
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const { register, getDashboardPath } = useContext(AuthContext);
  const navigate = useNavigate();

  // Countdown for resend verification email
  useEffect(() => {
    if (!successMessage || !registeredEmail || resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [successMessage, registeredEmail, resendCooldown]);

  const totalSteps = 5;

  const validateFirstName = (firstName) => {
    if (!firstName) {
      return t('common.nameRequired');
    }
    if (firstName.length < 2) {
      return t('common.nameMinLength');
    }
    if (/[0-9]/.test(firstName)) {
      return t('common.nameNoNumbers');
    }
    return '';
  };

  const validateLastName = (lastName) => {
    if (!lastName) {
      return t('common.nameRequired');
    }
    if (lastName.length < 2) {
      return t('common.nameMinLength');
    }
    if (/[0-9]/.test(lastName)) {
      return t('common.nameNoNumbers');
    }
    return '';
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return t('common.emailRequired');
    }
    if (!emailRegex.test(email)) {
      return t('common.emailInvalid');
    }
    return '';
  };

  const validatePassword = (password) => {
    if (!password) {
      return t('common.passwordRequired');
    }
    if (password.length < 6) {
      return t('common.passwordMinLength');
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (phone && phone.trim() !== '') {
      const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
      if (!phoneRegex.test(phone)) {
        return t('common.phoneInvalid');
      }
    }
    return '';
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) {
      return t('common.confirmPasswordRequired');
    }
    if (confirmPassword !== password) {
      return t('common.passwordsDoNotMatch');
    }
    return '';
  };

  const validateNationality = (nationality) => {
    if (!nationality) {
      return t('common.nationalityRequired');
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    
    // Clear error for this field when user starts typing
    const updatedErrors = { ...fieldErrors };
    if (updatedErrors[name]) {
      updatedErrors[name] = '';
    }
    
    // If password changes and confirmPassword has a value, re-validate confirmPassword
    if (name === 'password' && formData.confirmPassword) {
      updatedErrors.confirmPassword = validateConfirmPassword(formData.confirmPassword, value);
    }
    
    setFieldErrors(updatedErrors);
  };

  const validateCurrentStep = () => {
    let errors = {};
    
    switch (currentStep) {
      case 1:
        errors.firstName = validateFirstName(formData.firstName);
        errors.lastName = validateLastName(formData.lastName);
        break;
      case 2:
        errors.email = validateEmail(formData.email);
        break;
      case 3:
        errors.password = validatePassword(formData.password);
        errors.confirmPassword = validateConfirmPassword(formData.confirmPassword, formData.password);
        break;
      case 4:
        errors.nationality = validateNationality(formData.nationality);
        break;
      case 5:
        errors.phone = validatePhone(formData.phone);
        break;
      default:
        break;
    }
    
    setFieldErrors({ ...fieldErrors, ...errors });
    return !Object.values(errors).some(error => error !== '');
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';
    
    switch (name) {
      case 'firstName':
        error = validateFirstName(value);
        break;
      case 'lastName':
        error = validateLastName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'confirmPassword':
        error = validateConfirmPassword(value, formData.password);
        break;
      case 'phone':
        error = validatePhone(value);
        break;
      case 'nationality':
        error = validateNationality(value);
        break;
      default:
        break;
    }
    
    setFieldErrors({ ...fieldErrors, [name]: error });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const firstNameError = validateFirstName(formData.firstName);
    const lastNameError = validateLastName(formData.lastName);
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    const confirmPasswordError = validateConfirmPassword(formData.confirmPassword, formData.password);
    const phoneError = validatePhone(formData.phone);
    const nationalityError = validateNationality(formData.nationality);

    if (firstNameError || lastNameError || emailError || passwordError || confirmPasswordError || phoneError || nationalityError) {
      setFieldErrors({
        firstName: firstNameError,
        lastName: lastNameError,
        email: emailError,
        password: passwordError,
        confirmPassword: confirmPasswordError,
        phone: phoneError,
        nationality: nationalityError
      });
      return;
    }

    setLoading(true);
    try {
      // Combine firstName and lastName into name for the API; role is already property_owner
      const registrationData = {
        ...formData,
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        role: 'property_owner',
      };
      const result = await register(registrationData);
      // Property owner registration requires email verification – no token returned
      if (result?.needsVerification) {
        setError('');
        setRegisteredEmail(formData.email || '');
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setResendStatus('idle');
        setFormData({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '', phone: '', countryCode: '+20', nationality: '', role: 'property_owner' });
        setCurrentStep(1);
        // Show success state: message and link to login
        setSuccessMessage(getTranslatedMessage(result.message || 'Property owner registered successfully. Please check your email to verify your account.'));
        return;
      }
      // If token was returned (e.g. client or other role), go to dashboard
      const roleMap = {
        super_admin: '/super-admin',
        admin: '/admin',
        operations: '/operations',
        property_owner: '/owner',
        client: '/client',
        housekeeping: '/housekeeping',
      };
      const dashboardPath = roleMap[result?.role] || '/owner';
      navigate(dashboardPath);
    } catch (err) {
      const apiMessage = err.response?.data?.message || err.response?.data?.error;
      setError(getTranslatedMessage(apiMessage || ''));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ width: '100%', maxWidth: '100vw', overflow: 'hidden', overflowX: 'hidden', boxSizing: 'border-box', position: 'static' }}>
      {/* Full Page Video Background */}
      <div className="fixed inset-0 w-full h-full z-0" style={{ width: '100%', height: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
        <video
          className="absolute top-0 left-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          style={{ 
            width: '100%', 
            height: '100%',
            maxWidth: '100vw',
            objectFit: 'cover'
          }}
        >
          {/* Video source from assets */}
          <source src={backgroundVideo} type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          Your browser does not support the video tag.
        </video>
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1D2A3B]/90 via-[#1D2A3B]/80 to-[#1D2A3B]/90"></div>
      </div>

      <Navbar />

      {/* Page Header */}
      <div className="relative z-10 pt-32 pb-6 sm:pb-8 px-4 sm:px-6 lg:px-8 text-center" style={{ width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
        <div className="w-full max-w-7xl mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight px-2" style={{ color: '#D5B577' }}>
            {t('registerOwner.title')}
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 mb-4 leading-relaxed max-w-3xl mx-auto px-2">
            {t('registerOwner.subtitle')}
          </p>
        </div>
      </div>

      {/* Hero Section with Registration Form */}
      <section className="relative z-10 pt-4 sm:pt-8 pb-12 sm:pb-20 px-0" style={{ width: '100%', maxWidth: '100vw', minHeight: 'auto', boxSizing: 'border-box', overflow: 'hidden' }}>

        <div className="relative z-10 w-full" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <div className="flex flex-col lg:flex-row items-start lg:items-end" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', alignItems: 'flex-end' }}>
            {/* Left Side - Intro Content (First on mobile, left on desktop) */}
            <div className={`px-4 sm:px-6 lg:px-4 xl:px-6 w-full ${language === 'ar' ? 'text-right' : 'text-center lg:text-left'}`} style={{ width: isDesktop ? '60%' : '100%', maxWidth: '100%', boxSizing: 'border-box', flexShrink: 0, direction: language === 'ar' ? 'rtl' : 'ltr', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight" style={{ color: '#D5B577' }}>
                {t('registerOwner.becomePartner')}
              </h2>
              <p className="text-lg sm:text-xl md:text-2xl text-gray-200 mb-4 sm:mb-6 leading-relaxed">
                {t('common.startJourney')}
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start mb-6 sm:mb-8 lg:mb-4">
                <div className="flex items-center gap-2 text-sm sm:text-base text-gray-300">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{t('common.maximizeIncome')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm sm:text-base text-gray-300">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{t('common.professionalTools')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm sm:text-base text-gray-300">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{t('common.support247')}</span>
                </div>
              </div>

              {/* Video Player - How It Works */}
              <div className="mb-6 sm:mb-0 lg:mb-0 flex justify-center lg:justify-start" style={{ marginTop: 'auto' }}>
                <div 
                  className="rounded-xl overflow-hidden shadow-2xl border"
                  style={{
                    width: '100%',
                    maxWidth: '560px',
                    aspectRatio: '16/9',
                    backgroundColor: '#000',
                    borderColor: 'rgba(213, 181, 119, 0.3)',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(213, 181, 119, 0.2)'
                  }}
                >
                  <video
                    src="http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
                    controls
                    className="w-full h-full object-cover"
                    style={{ outline: 'none' }}
                    poster="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>

            {/* Right Side - Registration Form (Second on mobile, right on desktop) */}
            <div className="w-full px-4 sm:px-6 lg:px-3 xl:px-4 flex items-end" style={{ width: isDesktop ? '40%' : '100%', maxWidth: '100%', boxSizing: 'border-box', flexShrink: 0, alignSelf: 'flex-end' }}>
              {/* Registration Card */}
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border p-4 sm:p-5 w-full" style={{ borderColor: 'rgba(213, 181, 119, 0.3)', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            {successMessage && (
              <div className="p-4 rounded-xl bg-green-500/20 border border-green-500/50 text-green-200 text-sm flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {successMessage}
                </div>
                <div className="mt-2 pt-2 border-t border-green-500/30">
                  {resendCooldown > 0 ? (
                    <p className="text-xs text-green-200/90">
                      {t('registerOwner.resendIn', { count: resendCooldown })}
                    </p>
                  ) : (
                    <button
                      type="button"
                      disabled={resendStatus === 'sending'}
                      onClick={async () => {
                        if (!registeredEmail) return;
                        setResendStatus('sending');
                        try {
                          await api.post('/api/property-owner/resend-verification', { email: registeredEmail });
                          setResendStatus('sent');
                          setResendCooldown(RESEND_COOLDOWN_SECONDS);
                        } catch (err) {
                          setResendStatus('error');
                        }
                      }}
                      className="w-full py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60"
                      style={{ borderColor: 'rgba(213, 181, 119, 0.6)', color: '#D5B577', backgroundColor: 'rgba(213, 181, 119, 0.1)' }}
                    >
                      {resendStatus === 'sending' ? t('registerOwner.sending') : resendStatus === 'sent' ? t('registerOwner.emailSentAgain') : resendStatus === 'error' ? t('registerOwner.resendFailed') : t('registerOwner.resendVerification')}
                    </button>
                  )}
                </div>
              </div>
            )}
            {error && (
              <div className="p-4 rounded-xl bg-red-500/20 border border-red-500/50 text-red-300 text-sm flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}

            {!successMessage && (
            <>
            {/* Progress Indicator */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                {[...Array(totalSteps)].map((_, index) => (
                  <div key={index} className="flex items-center flex-1">
                    <div
                      className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                        index + 1 <= currentStep
                          ? 'bg-[#D5B577] text-[#1D2A3B]'
                          : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      {index + 1 < currentStep ? (
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    {index < totalSteps - 1 && (
                      <div
                        className={`flex-1 h-0.5 sm:h-1 mx-0.5 sm:mx-1 transition-all ${
                          index + 1 < currentStep ? 'bg-[#D5B577]' : 'bg-gray-600'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 text-center">
                {t('registerOwner.stepOf', { current: currentStep, total: totalSteps })}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-2.5" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              {/* Step 1: First Name and Last Name */}
              {currentStep === 1 && (
              <div className="grid grid-cols-2 gap-2.5" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                  <label className="block text-xs font-semibold mb-1" style={{ color: '#D5B577' }}>
                    {t('common.firstName')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <svg className="w-3.5 h-3.5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder={t('common.enterFirstName')}
                      className="w-full pl-8 pr-2.5 py-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-xs"
                      style={{
                        backgroundColor: 'rgba(29, 42, 59, 0.7)',
                        border: fieldErrors.firstName ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                        boxSizing: 'border-box',
                        width: '100%',
                        maxWidth: '100%',
                        height: '38px'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (!fieldErrors.firstName) {
                          e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        }
                      }}
                      required
                    />
                  </div>
                  {fieldErrors.firstName && (
                    <p className="mt-0.5 text-xs text-red-400">{fieldErrors.firstName}</p>
                  )}
                </div>
                <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                  <label className="block text-xs font-semibold mb-1" style={{ color: '#D5B577' }}>
                    {t('common.lastName')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <svg className="w-3.5 h-3.5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder={t('common.enterLastName')}
                      className="w-full pl-8 pr-2.5 py-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-xs"
                      style={{
                        backgroundColor: 'rgba(29, 42, 59, 0.7)',
                        border: fieldErrors.lastName ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                        boxSizing: 'border-box',
                        width: '100%',
                        maxWidth: '100%',
                        height: '38px'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (!fieldErrors.lastName) {
                          e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        }
                      }}
                      required
                    />
                  </div>
                  {fieldErrors.lastName && (
                    <p className="mt-0.5 text-xs text-red-400">{fieldErrors.lastName}</p>
                  )}
                </div>
              </div>
              )}

              {/* Step 2: Email */}
              {currentStep === 2 && (
              <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <label className="block text-xs font-semibold mb-1" style={{ color: '#D5B577' }}>
                  {t('registerOwner.form.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                    <svg className="w-3.5 h-3.5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('common.enterEmail')}
                    className="w-full pl-8 pr-2.5 py-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-xs"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.email ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      boxSizing: 'border-box',
                      width: '100%',
                      maxWidth: '100%',
                      height: '38px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!fieldErrors.email) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                      }
                    }}
                    required
                  />
                </div>
                {fieldErrors.email && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.email}</p>
                )}
              </div>
              )}

              {/* Step 3: Password and Confirm Password */}
              {currentStep === 3 && (
                <>
              <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <label className="block text-xs font-semibold mb-1" style={{ color: '#D5B577' }}>
                  {t('register.password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                    <svg className="w-3.5 h-3.5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t('register.password')}
                    className="w-full pl-8 pr-8 py-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-xs"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.password ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      boxSizing: 'border-box',
                      width: '100%',
                      maxWidth: '100%',
                      height: '38px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!fieldErrors.password) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                      }
                    }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-2.5 flex items-center"
                    style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    {showPassword ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.password}</p>
                )}
              </div>

              <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <label className="block text-xs font-semibold mb-1" style={{ color: '#D5B577' }}>
                  {t('common.reEnterPassword')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                    <svg className="w-3.5 h-3.5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder={t('common.reEnterPassword')}
                    className="w-full pl-8 pr-8 py-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-xs"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.confirmPassword ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      boxSizing: 'border-box',
                      width: '100%',
                      maxWidth: '100%',
                      height: '38px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!fieldErrors.confirmPassword) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                      }
                    }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-2.5 flex items-center"
                    style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    {showConfirmPassword ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.confirmPassword}</p>
                )}
              </div>
                </>
              )}

              {/* Step 4: Nationality */}
              {currentStep === 4 && (
              <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <label className="block text-xs font-semibold mb-1" style={{ color: '#D5B577' }}>
                  {t('common.nationality')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                    <svg className="w-3.5 h-3.5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <select
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    className="w-full pl-8 pr-7 py-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 appearance-none text-xs"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.nationality ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      color: formData.nationality ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                      boxSizing: 'border-box',
                      width: '100%',
                      maxWidth: '100%',
                      height: '38px',
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%23D5B577\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.4rem center',
                      backgroundSize: '1em 1em',
                      paddingRight: '1.5rem'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#D5B577';
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!fieldErrors.nationality) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(29, 42, 59, 0.7)';
                      }
                    }}
                    required
                  >
                    <option value="" disabled style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{t('common.selectNationality')}</option>
                    <option value="Egyptian">{t('common.nationalities.egyptian')}</option>
                    <option value="Saudi Arabian">{t('common.nationalities.saudiArabian')}</option>
                    <option value="Emirati">{t('common.nationalities.emirati')}</option>
                    <option value="Kuwaiti">{t('common.nationalities.kuwaiti')}</option>
                    <option value="Qatari">{t('common.nationalities.qatari')}</option>
                    <option value="Omani">{t('common.nationalities.omani')}</option>
                    <option value="Bahraini">{t('common.nationalities.bahraini')}</option>
                    <option value="Jordanian">{t('common.nationalities.jordanian')}</option>
                    <option value="Lebanese">{t('common.nationalities.lebanese')}</option>
                    <option value="Syrian">{t('common.nationalities.syrian')}</option>
                    <option value="Iraqi">{t('common.nationalities.iraqi')}</option>
                    <option value="Palestinian">{t('common.nationalities.palestinian')}</option>
                    <option value="Yemeni">{t('common.nationalities.yemeni')}</option>
                    <option value="Sudanese">{t('common.nationalities.sudanese')}</option>
                    <option value="Libyan">Libyan</option>
                    <option value="Tunisian">Tunisian</option>
                    <option value="Algerian">Algerian</option>
                    <option value="Moroccan">Moroccan</option>
                    <option value="American">American</option>
                    <option value="British">British</option>
                    <option value="Canadian">Canadian</option>
                    <option value="French">French</option>
                    <option value="German">German</option>
                    <option value="Italian">Italian</option>
                    <option value="Spanish">Spanish</option>
                    <option value="Australian">Australian</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {fieldErrors.nationality && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.nationality}</p>
                )}
              </div>
              )}

              {/* Step 5: Phone */}
              {currentStep === 5 && (
              <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <label className="block text-xs font-semibold mb-1" style={{ color: '#D5B577' }}>
                  {t('registerOwner.form.phone')} <span className="text-gray-500 text-xs">({t('common.optional')})</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative w-[100px] min-w-[100px]">
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="w-full px-2 py-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-white text-xs appearance-none"
                      style={{
                        backgroundColor: 'rgba(29, 42, 59, 0.7)',
                        border: '1px solid rgba(213, 181, 119, 0.3)',
                        boxSizing: 'border-box',
                        width: '100%',
                        maxWidth: '100%',
                        height: '38px',
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%23D5B577\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.3rem center',
                        backgroundSize: '0.9em 0.9em',
                        paddingRight: '1.3rem'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#D5B577';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(29, 42, 59, 0.7)';
                      }}
                    >
                      <option value="+20">🇪🇬 +20</option>
                      <option value="+966">🇸🇦 +966</option>
                      <option value="+971">🇦🇪 +971</option>
                      <option value="+965">🇰🇼 +965</option>
                      <option value="+974">🇶🇦 +974</option>
                      <option value="+968">🇴🇲 +968</option>
                      <option value="+973">🇧🇭 +973</option>
                      <option value="+962">🇯🇴 +962</option>
                      <option value="+961">🇱🇧 +961</option>
                      <option value="+963">🇸🇾 +963</option>
                      <option value="+964">🇮🇶 +964</option>
                      <option value="+970">🇵🇸 +970</option>
                      <option value="+967">🇾🇪 +967</option>
                      <option value="+249">🇸🇩 +249</option>
                      <option value="+218">🇱🇾 +218</option>
                      <option value="+216">🇹🇳 +216</option>
                      <option value="+213">🇩🇿 +213</option>
                      <option value="+212">🇲🇦 +212</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+1">🇨🇦 +1</option>
                      <option value="+33">🇫🇷 +33</option>
                      <option value="+49">🇩🇪 +49</option>
                      <option value="+39">🇮🇹 +39</option>
                      <option value="+34">🇪🇸 +34</option>
                      <option value="+61">🇦🇺 +61</option>
                    </select>
                  </div>
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <svg className="w-3.5 h-3.5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('register.phone')}
                      className="w-full pl-8 pr-2.5 py-2 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-xs"
                      style={{
                        backgroundColor: 'rgba(29, 42, 59, 0.7)',
                        border: fieldErrors.phone ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                        boxSizing: 'border-box',
                        width: '100%',
                        maxWidth: '100%',
                        height: '38px'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (!fieldErrors.phone) {
                          e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        }
                      }}
                    />
                  </div>
                </div>
                {fieldErrors.phone && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.phone}</p>
                )}
              </div>
              )}

              {/* Terms & Conditions - Only show on last step */}
              {currentStep === totalSteps && (
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    className="w-3 h-3 mt-0.5 rounded"
                    style={{ accentColor: '#D5B577' }}
                    required
                  />
                  <label className="ml-2 text-xs text-gray-300 leading-tight">
                    {t('common.agreeToTerms')}{' '}
                    <Link to="/terms/owner" className="underline" style={{ color: '#D5B577' }}>
                      {t('common.termsConditions')}
                    </Link>{' '}
                    {t('common.and', 'and')}{' '}
                    <Link to="/privacy" className="underline" style={{ color: '#D5B577' }}>
                      {t('common.privacyPolicy')}
                    </Link>
                  </label>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex gap-2 pt-2">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="flex-1 py-2.5 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-sm border-2"
                    style={{ 
                      borderColor: '#D5B577', 
                      color: '#D5B577',
                      backgroundColor: 'transparent',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'rgba(213, 181, 119, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    {t('registerOwner.previous')}
                  </button>
                )}
                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="flex-1 py-2.5 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-sm"
                    style={{ 
                      backgroundColor: '#D5B577', 
                      color: '#1D2A3B',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#c4a366'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#D5B577'}
                  >
                    {t('registerOwner.next')}
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-2.5 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-sm disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                    style={{ 
                      backgroundColor: '#D5B577', 
                      color: '#1D2A3B',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#c4a366')}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = '#D5B577')}
                  >
                    {loading ? t('registerOwner.loading') : t('common.registerAsOwner')}
                  </button>
                )}
              </div>
            </form>

            <div className="mt-4 sm:mt-6 space-y-2 text-center" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <p className="text-gray-400 text-xs sm:text-sm">
                {t('register.haveAccount')}{' '}
                <a
                  href="https://admin.hostaglobal.com"
                  className="font-semibold transition-colors cursor-pointer"
                  style={{ color: '#D5B577' }}
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = 'https://admin.hostaglobal.com';
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  {t('register.signIn')}
                </a>
              </p>
              {/* <p className="text-gray-400 text-xs sm:text-sm">
                {t('common.wantToBook')}{' '}
                <Link
                  to="/register"
                  className="font-semibold transition-colors"
                  style={{ color: '#D5B577' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  {t('common.signUpAsGuest')}
                </Link>
              </p> */}
            </div>
            </>
            )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default RegisterOwner;
